import { useGifStore } from './gif-store';
import { AspectRatioPreset, CropRegion, getAspectRatioValue } from './types';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Crop, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const ASPECT_RATIOS: { value: AspectRatioPreset; label: string }[] = [
  { value: 'free', label: 'Free' },
  { value: '1:1', label: '1:1 Square' },
  { value: '16:9', label: '16:9' },
  { value: '4:3', label: '4:3' },
  { value: '9:16', label: '9:16 Story' },
];

const SOCIAL_PRESETS: { label: string; ratio: AspectRatioPreset; widthHint: string }[] = [
  { label: 'Twitter / X', ratio: '16:9', widthHint: '480x270' },
  { label: 'Instagram Square', ratio: '1:1', widthHint: '480x480' },
  { label: 'Instagram Story', ratio: '9:16', widthHint: '360x640' },
  { label: 'Discord Emoji', ratio: '1:1', widthHint: '256x256' },
];

/**
 * Build a centered crop region for a given aspect ratio (relative to source 1:1 box).
 * Uses normalized coordinates 0-1.
 */
function buildCenteredCrop(aspect: AspectRatioPreset): CropRegion {
  const ratio = getAspectRatioValue(aspect);
  if (ratio === null) {
    return { x: 0, y: 0, width: 1, height: 1, aspectRatio: 'free' };
  }
  // Assume source is roughly 16:9, pick the largest centered rect with target ratio
  const sourceRatio = 16 / 9;
  let w = 1;
  let h = 1;
  if (ratio < sourceRatio) {
    // Target narrower than source: limit width
    w = ratio / sourceRatio;
    h = 1;
  } else {
    // Target wider than source: limit height
    w = 1;
    h = sourceRatio / ratio;
  }
  return {
    x: (1 - w) / 2,
    y: (1 - h) / 2,
    width: w,
    height: h,
    aspectRatio: aspect,
  };
}

/**
 * F5: Crop & Resize controls.
 * Aspect ratio presets, social platform sizes, and a visual indicator of the crop area.
 */
export function CropEditor() {
  const crop = useGifStore((s) => s.settings.crop);
  const maxWidth = useGifStore((s) => s.settings.maxWidth);
  const setCrop = useGifStore((s) => s.setCrop);
  const updateSettings = useGifStore((s) => s.updateSettings);

  const handleSelectAspect = (aspect: AspectRatioPreset) => {
    if (aspect === 'free') {
      setCrop(undefined);
    } else {
      setCrop(buildCenteredCrop(aspect));
    }
  };

  const handleClear = () => setCrop(undefined);

  const handleSocialPreset = (ratio: AspectRatioPreset, widthHint: string) => {
    const [w] = widthHint.split('x').map(Number);
    setCrop(buildCenteredCrop(ratio));
    if (Number.isFinite(w)) {
      updateSettings({ maxWidth: w });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crop className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Crop & Resize</CardTitle>
          </div>
          {crop && (
            <Button variant="ghost" size="sm" onClick={handleClear} className="h-7 gap-1">
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          )}
        </div>
        <CardDescription>Adjust output dimensions and aspect ratio</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Aspect ratio buttons */}
        <div>
          <Label className="text-xs">Aspect Ratio</Label>
          <div className="mt-2 grid grid-cols-5 gap-1.5">
            {ASPECT_RATIOS.map((ar) => {
              const isActive = ar.value === 'free' ? !crop : crop?.aspectRatio === ar.value;
              return (
                <button
                  key={ar.value}
                  type="button"
                  onClick={() => handleSelectAspect(ar.value)}
                  className={cn(
                    'flex flex-col items-center justify-center rounded-md border-2 p-2 text-xs font-medium transition-all',
                    isActive
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-muted hover:border-muted-foreground/30',
                  )}
                >
                  {ar.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Custom width */}
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="custom-width" className="text-xs">Output Width</Label>
            <span className="text-xs text-muted-foreground">{maxWidth}px</span>
          </div>
          <input
            id="custom-width"
            type="range"
            min={100}
            max={1280}
            step={10}
            value={maxWidth}
            onChange={(e) => updateSettings({ maxWidth: Number(e.target.value), quality: 'custom' })}
            className="mt-2 w-full accent-primary"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>100px</span>
            <span>1280px</span>
          </div>
          {maxWidth > 800 && (
            <p className="mt-1 text-[11px] text-amber-600">
              Large outputs produce big files. Consider reducing width for sharing.
            </p>
          )}
        </div>

        {/* Social presets */}
        <div>
          <Label className="text-xs">Social Platform Presets</Label>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {SOCIAL_PRESETS.map((p) => (
              <Button
                key={p.label}
                type="button"
                variant="outline"
                size="sm"
                className="h-auto flex-col items-start gap-0.5 py-2 text-left"
                onClick={() => handleSocialPreset(p.ratio, p.widthHint)}
              >
                <span className="text-xs font-medium">{p.label}</span>
                <span className="text-[10px] text-muted-foreground">{p.widthHint}</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Visual indicator */}
        {crop && (
          <div className="rounded-lg border border-border p-3">
            <Label className="text-xs">Crop Preview</Label>
            <div className="mt-2 relative aspect-video w-full bg-muted/40 rounded">
              <div
                className="absolute border-2 border-primary bg-primary/10"
                style={{
                  left: `${crop.x * 100}%`,
                  top: `${crop.y * 100}%`,
                  width: `${crop.width * 100}%`,
                  height: `${crop.height * 100}%`,
                }}
              />
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              {Math.round(crop.width * 100)}% × {Math.round(crop.height * 100)}% of source · {crop.aspectRatio}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CropEditor;
